/** Splits a sentence into tappable tokens for the reader. */
import type { Sentence, Token, Word } from './content.ts';
import { containsHan } from './answer.ts';

const PUNCT_ONLY_RE = /^[\p{P}\p{S}\s]+$/u;

function punct(surface: string): Token {
  return { surface, punct: true };
}

function fromWord(surface: string, word: Word): Token {
  return {
    surface,
    wordId: word.id,
    ru: word.ru,
    ...(word.pinyin ? { pinyin: word.pinyin } : {}),
  };
}

/** Greedy longest match of lesson words; unmatched characters become single plain tokens. */
function segmentChinese(text: string, words: readonly Word[]): Token[] {
  const byLength = [...words].sort((a, b) => b.word.length - a.word.length);
  const tokens: Token[] = [];
  let plain = '';
  const flushPlain = () => {
    for (const ch of Array.from(plain)) tokens.push(PUNCT_ONLY_RE.test(ch) ? punct(ch) : { surface: ch });
    plain = '';
  };
  let i = 0;
  while (i < text.length) {
    const match = byLength.find((w) => w.word.length > 0 && text.startsWith(w.word, i));
    if (match) {
      flushPlain();
      tokens.push(fromWord(match.word, match));
      i += match.word.length;
    } else {
      plain += text[i];
      i += 1;
    }
  }
  flushPlain();
  return tokens;
}

/** Splits on word boundaries, keeping spaces attached to the preceding token for layout. */
function segmentLatin(text: string, words: readonly Word[]): Token[] {
  const lookup = new Map(words.map((w) => [w.word.toLowerCase(), w]));
  const parts = text.match(/[\p{L}\p{N}'’-]+|[^\p{L}\p{N}'’-]+/gu) ?? [];
  return parts.map((part) => {
    if (PUNCT_ONLY_RE.test(part)) return punct(part);
    const word = lookup.get(part.toLowerCase().replace(/’/g, "'"));
    return word ? fromWord(part, word) : { surface: part };
  });
}

export function tokensFor(sentence: Sentence, words: readonly Word[]): Token[] {
  if (sentence.tokensStatus === 'done' && sentence.tokens.length > 0) return sentence.tokens;
  return containsHan(sentence.text)
    ? segmentChinese(sentence.text, words)
    : segmentLatin(sentence.text, words);
}
